import { useState } from 'react';
import type { ReactNode } from 'react';
import type { DayView, EntryRow } from '../lib/day';
import { advanceTotal, allSigned, dayApi, sectionEditable } from '../lib/day';
import CostCodeSelect from '../components/CostCodeSelect';
import { formatDate, formatHours, formatRs } from '../lib/money';
import { approvalsApi } from '../lib/approvals';
import { useIsPhone } from '../lib/viewport';
import type { CostCode } from '../lib/costcodes';
import { codeOf } from '../lib/costcodes';

/**
 * The owner's timesheet: read-only, with the workings in small text under the
 * figures — `Rs 563/h` under OT pay, the reason under the allowance, `6 of 8`
 * under a short day.
 *
 * Ten columns, the same count as the clerk's:
 *   Worker · Job done · Hrs · OT · Salary · OT pay · Allowance · Earned ·
 *   Advance · Approval
 *
 * The Sunday allowance sits inside the allowance cell rather than adding an
 * eleventh column — the prototype adds one here, which this deliberately does
 * not (CLAUDE.md, "Corrections to the prototype", row 5).
 *
 * Absentees are summarised on one line in the footer rather than taking a row
 * each.
 *
 * "He checks and approves, often from his own phone" — so under 700px the rows
 * become cards too. Nothing is hidden behind a tap here: it is a read, and the
 * workings are the whole point of the screen.
 */

interface Props {
  view: DayView;
  costCodes: CostCode[];
  onChanged: () => void;
  onError: (message: string) => void;
}

export default function OwnerSheet({ view, costCodes, onChanged, onError }: Props) {
  const [busy, setBusy] = useState<string | null>(null);
  const date = view.day.workDate;
  const phone = useIsPhone();

  const worked = view.entries.filter((entry) => entry.attendance === 'worked');
  const absent = view.entries.filter((entry) => entry.attendance === 'absent');
  const notEntered = view.entries.filter((entry) => entry.attendance === null);
  const pending = worked.filter((entry) => entry.status !== 'approved').length;

  async function act(workerId: string, work: () => Promise<unknown>) {
    setBusy(workerId);
    try {
      await work();
      onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'That did not work.');
      onChanged();
    } finally {
      setBusy(null);
    }
  }

  // The owner may change a man's cost code here until his row is approved —
  // the one thing on this sheet he edits (11 October 2026). Once approved it
  // shows as text; undo the approval to change it.
  const sectionOpen = sectionEditable(view, 'labour', true);
  const codePicker = (entry: EntryRow): ReactNode => {
    if (costCodes.length === 0 && !entry.costCodeId) return null;
    if (!sectionOpen || entry.status === 'approved') {
      const code = codeOf(costCodes, entry.costCodeId);
      return code ? <div className="why">{code}</div> : null;
    }
    return (
      <div style={{ marginTop: 4 }}>
        <CostCodeSelect
          className="cc"
          compact
          codes={costCodes}
          value={entry.costCodeId}
          usual={entry.usualCostCodeId}
          disabled={busy === entry.workerId}
          onChange={(costCodeId) =>
            void act(entry.workerId, () =>
              approvalsApi.setCostCode(date, entry.workerId, costCodeId, entry.rowVersion),
            )
          }
        />
      </div>
    );
  };

  // An advance the office put on the wrong man is cancelled here, with a
  // reason (1 November 2026). Voided, never deleted — it stays in the audit
  // trail. Not on an approved row: undo the approval first.
  const advanceList = (entry: EntryRow): ReactNode => (
    <AdvanceList
      entry={entry}
      canCancel={sectionOpen && entry.status !== 'approved'}
      busy={busy === entry.workerId}
      onCancel={(id, reason) =>
        act(entry.workerId, () => dayApi.voidAdvance(date, id, reason))
      }
    />
  );

  /** Approving is pinned to the versions on screen, not to whatever is live. */
  const onScreen = () =>
    view.entries
      .filter((entry) => entry.attendance !== null)
      .map((entry) => ({ workerId: entry.workerId, rowVersion: entry.rowVersion }));

  return (
    <div className="card">
      <div className="row" style={{ paddingTop: 0 }}>
        <div>
          <h3>
            Time sheet — {formatDate(date)}
            {view.day.isSunday && <span className="pill p-wait" style={{ marginLeft: 8 }}>Sunday</span>}
          </h3>
          <div className="sub">
            {worked.length} worked · {absent.length} absent
            {notEntered.length > 0 && ` · ${notEntered.length} not entered`} ·{' '}
            {formatRs(view.totals.labourRs)}
          </div>
        </div>
        <div className="stat">
          <div className="name">{pending}</div>
          <div className="sub">to approve</div>
        </div>
      </div>

      {worked.length === 0 ? (
        <div className="empty">Nothing submitted for this day.</div>
      ) : phone ? (
        <>
          <div className="wcards">
            {worked.map((entry) => (
              <OwnerCard
                key={entry.workerId}
                entry={entry}
                code={codePicker(entry)}
                advanceList={advanceList(entry)}
                busy={busy === entry.workerId}
                onApprove={() =>
                  void act(entry.workerId, () =>
                    approvalsApi.approveEntry(date, entry.workerId, entry.rowVersion),
                  )
                }
                onUndo={() =>
                  void act(entry.workerId, () => approvalsApi.undoEntry(date, entry.workerId))
                }
                onReject={() =>
                  void act(entry.workerId, () => approvalsApi.rejectEntry(date, entry.workerId))
                }
              />
            ))}
          </div>

          <div className="wtotals">
            <div>
              {worked.length} employees
              {absent.length > 0 && (
                <> · absent: {absent.map((entry) => entry.workerName).join(', ')}</>
              )}
            </div>
            <div className="wtotals-money">
              <span>
                <span className="tile-label">Labour</span>
                {formatRs(view.totals.labourRs)}
              </span>
              <span>
                <span className="tile-label">Advances</span>
                {formatRs(view.totals.advancesRs)}
              </span>
            </div>
          </div>
        </>
      ) : (
        <div className="scroll">
          <table className="tt">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Job done</th>
                <th className="num">Hrs</th>
                <th className="num">OT</th>
                <th className="num">Salary</th>
                <th className="num">OT pay</th>
                <th className="num">Allowance</th>
                <th className="num">Earned</th>
                <th className="num">Advance</th>
                <th style={{ textAlign: 'right' }}>Approval</th>
              </tr>
            </thead>
            <tbody>
              {worked.map((entry) => (
                <SheetRow
                  key={entry.workerId}
                  entry={entry}
                  code={codePicker(entry)}
                  advanceList={advanceList(entry)}
                  busy={busy === entry.workerId}
                  onApprove={() =>
                    void act(entry.workerId, () =>
                      approvalsApi.approveEntry(date, entry.workerId, entry.rowVersion),
                    )
                  }
                  onUndo={() =>
                    void act(entry.workerId, () => approvalsApi.undoEntry(date, entry.workerId))
                  }
                  onReject={() =>
                    void act(entry.workerId, () => approvalsApi.rejectEntry(date, entry.workerId))
                  }
                />
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan={7}>
                  {worked.length} employees
                  {absent.length > 0 && (
                    <> · absent: {absent.map((entry) => entry.workerName).join(', ')}</>
                  )}
                </th>
                <th className="num">{formatRs(view.totals.labourRs)}</th>
                <th className="num">{formatRs(view.totals.advancesRs)}</th>
                <th />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {pending > 0 && (
        <div className="bar" style={{ marginTop: 12 }}>
          <button
            type="button"
            className="btn pri"
            disabled={busy !== null}
            onClick={() =>
              void act('all', () => approvalsApi.approveAll(date, onScreen()))
            }
          >
            Approve all {pending} remaining
          </button>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------- one card

/**
 * The same row on a phone. Every figure the table shows is here, with its
 * working beneath it — the owner is checking arithmetic, so the `6 of 8` and
 * the `Rs 563/h` matter more on the small screen, not less.
 */
function OwnerCard({
  entry, code, advanceList, busy, onApprove, onUndo, onReject,
}: {
  entry: EntryRow;
  /** The day's cost code: a picker until the row is approved, then text. */
  code: ReactNode;
  advanceList: ReactNode;
  busy: boolean;
  onApprove: () => void;
  onUndo: () => void;
  onReject: () => void;
}) {
  const approved = entry.status === 'approved';
  const rejected = entry.status === 'rejected';
  const byDay = entry.payBasis === 'day';
  const advances = advanceTotal(entry);
  const allowance = entry.allowanceRs + entry.sundayAllowanceRs;

  return (
    <div className="wcard open">
      <div className="wcard-head" style={{ cursor: 'default' }}>
        <div className="wcard-who">
          <div className="wcard-name">{entry.workerName}</div>
          <div className="wcard-rate">
            {entry.roleName ?? '—'}
            {!byDay && ' · allowance only, no salary'}
            {code}
          </div>
        </div>
        <div className="wcard-sum">
          <div className="wcard-earned">{formatRs(entry.earnedRs)}</div>
          {approved ? (
            <span className="pill p-ok">Approved</span>
          ) : rejected ? (
            <span className="pill p-no">Sent back</span>
          ) : (
            <span className="pill p-wait">To check</span>
          )}
        </div>
      </div>

      <div className="wcard-body">
        <div className="figs">
          <div className="fig">
            <span className="tile-label">Hours</span>
            {byDay ? formatHours(entry.hoursTenths) : '—'}
          </div>
          <div className="fig">
            <span className="tile-label">Overtime</span>
            {entry.otTenths ? `${formatHours(entry.otTenths)}h` : '—'}
          </div>
          <div className="fig">
            <span className="tile-label">Salary</span>
            {entry.salaryRs ? formatRs(entry.salaryRs) : '—'}
            {entry.salaryRs > 0 && byDay && entry.hoursTenths !== 80 && (
              <div className="why">{formatHours(entry.hoursTenths)} of 8</div>
            )}
          </div>
          <div className="fig">
            <span className="tile-label">OT pay</span>
            {entry.otRs ? formatRs(entry.otRs) : '—'}
            {entry.otRs > 0 && (
              <div className="why">{formatRs(Math.round(entry.dailyRateRs / 8))}/h</div>
            )}
          </div>
          <div className="fig">
            <span className="tile-label">Allowance</span>
            {allowance ? formatRs(allowance) : '—'}
            {allowance > 0 && (
              <div className="why">
                {entry.allowanceReason ? (
                  entry.allowanceReason
                ) : (
                  <span style={{ color: 'var(--text-danger)' }}>no reason</span>
                )}
              </div>
            )}
            {entry.sundayAllowanceRs > 0 && (
              <div className="why">includes {formatRs(entry.sundayAllowanceRs)} Sunday</div>
            )}
          </div>
          <div className="fig">
            <span className="tile-label">Advance</span>
            {advances > 0 ? formatRs(advances) : '—'}
            {advances > 0 && (
              <div className="why">
                {allSigned(entry) ? (
                  'signature held'
                ) : (
                  <span style={{ color: 'var(--text-danger)' }}>no signature</span>
                )}
              </div>
            )}
          </div>
        </div>

        {advanceList}

        <div className="bar" style={{ marginTop: 10 }}>
          {approved ? (
            <button type="button" className="btn" disabled={busy} onClick={onUndo}>
              Undo approval
            </button>
          ) : rejected ? (
            <span className="sub">Sent back to the clerk.</span>
          ) : (
            <>
              <button type="button" className="btn dang" disabled={busy} onClick={onReject}>
                Reject
              </button>
              <button type="button" className="btn pri" disabled={busy} onClick={onApprove}>
                Approve
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- one row

function SheetRow({
  entry, code, advanceList, busy, onApprove, onUndo, onReject,
}: {
  entry: EntryRow;
  code: ReactNode;
  advanceList: ReactNode;
  busy: boolean;
  onApprove: () => void;
  onUndo: () => void;
  onReject: () => void;
}) {
  const approved = entry.status === 'approved';
  const rejected = entry.status === 'rejected';
  const byDay = entry.payBasis === 'day';
  const advances = advanceTotal(entry);
  const allowance = entry.allowanceRs + entry.sundayAllowanceRs;

  return (
    <tr>
      <td className="nm">{entry.workerName}</td>

      <td>
        {entry.roleName ?? '—'}
        {!byDay && <div className="why">allowance only, no salary</div>}
        {code}
      </td>

      <td className="num">{byDay ? formatHours(entry.hoursTenths) : '—'}</td>
      <td className="num">{entry.otTenths ? `${formatHours(entry.otTenths)}h` : '—'}</td>

      {/* `6 of 8` under a short day. */}
      <td className="num">
        {entry.salaryRs ? formatRs(entry.salaryRs) : '—'}
        {entry.salaryRs > 0 && byDay && entry.hoursTenths !== 80 && (
          <div className="why">{formatHours(entry.hoursTenths)} of 8</div>
        )}
      </td>

      {/* `Rs 563/h` under OT pay — the daily rate divided by 8. */}
      <td className="num">
        {entry.otRs ? formatRs(entry.otRs) : '—'}
        {entry.otRs > 0 && <div className="why">{formatRs(Math.round(entry.dailyRateRs / 8))}/h</div>}
      </td>

      {/* The reason under the allowance, and the Sunday money inside the same
          cell rather than in a column of its own. */}
      <td className="num">
        {allowance ? formatRs(allowance) : '—'}
        {allowance > 0 && (
          <div className="why">
            {entry.allowanceReason ? (
              entry.allowanceReason
            ) : (
              <span style={{ color: 'var(--text-danger)' }}>no reason</span>
            )}
          </div>
        )}
        {entry.sundayAllowanceRs > 0 && (
          <div className="why">includes {formatRs(entry.sundayAllowanceRs)} Sunday</div>
        )}
      </td>

      <td className="num nm">{formatRs(entry.earnedRs)}</td>

      <td className="num">
        {advances > 0 ? (
          <>
            {formatRs(advances)}
            <div className="why">
              {allSigned(entry) ? (
                'signature held'
              ) : (
                <span style={{ color: 'var(--text-danger)' }}>no signature</span>
              )}
            </div>
            {advanceList}
          </>
        ) : (
          '—'
        )}
      </td>

      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {approved ? (
          <>
            <span className="pill p-ok">Approved</span>{' '}
            <button
              type="button"
              className="btn sm"
              style={{ minWidth: 0 }}
              disabled={busy}
              onClick={onUndo}
            >
              Undo
            </button>
          </>
        ) : rejected ? (
          <span className="pill p-no">Sent back</span>
        ) : (
          <>
            <button
              type="button"
              className="btn sm dang"
              style={{ minWidth: 0 }}
              disabled={busy}
              onClick={onReject}
            >
              Reject
            </button>{' '}
            <button
              type="button"
              className="btn sm pri"
              style={{ minWidth: 0 }}
              disabled={busy}
              onClick={onApprove}
            >
              Approve
            </button>
          </>
        )}
      </td>
    </tr>
  );
}

// ------------------------------------------------------- cancel an advance

/**
 * Each advance on the row with "Cancel" beside it, for one the office put on
 * the wrong man. A reason is required — it goes to the audit trail with who
 * and when. Draws nothing once the row is approved or Labour is locked.
 */
function AdvanceList({
  entry, canCancel, busy, onCancel,
}: {
  entry: EntryRow;
  canCancel: boolean;
  busy: boolean;
  onCancel: (id: string, reason: string) => Promise<void>;
}) {
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);

  if (!canCancel || entry.advances.length === 0) return null;

  const close = () => {
    setCancelling(null);
    setReason('');
    setTried(false);
  };

  return (
    <div style={{ marginTop: 6, textAlign: 'right' }}>
      {entry.advances.map((advance) =>
        cancelling === advance.id ? (
          <div key={advance.id} style={{ marginTop: 4 }}>
            <div className="why">Cancel {formatRs(advance.amountRs)}?</div>
            <input
              type="text"
              placeholder="why is it cancelled"
              value={reason}
              autoFocus
              aria-invalid={tried && !reason.trim()}
              style={{ width: '100%', minWidth: 140, marginTop: 4 }}
              onChange={(e) => setReason(e.target.value)}
            />
            {tried && !reason.trim() && <div className="err">Write why it is cancelled.</div>}
            <div style={{ marginTop: 4, whiteSpace: 'nowrap' }}>
              <button type="button" className="btn sm" style={{ minWidth: 0 }} onClick={close}>
                Keep
              </button>{' '}
              <button
                type="button"
                className="btn sm dang"
                style={{ minWidth: 0 }}
                disabled={busy}
                onClick={() => {
                  setTried(true);
                  if (!reason.trim()) return;
                  void onCancel(advance.id, reason.trim()).then(close);
                }}
              >
                Cancel advance
              </button>
            </div>
          </div>
        ) : (
          <div key={advance.id} className="why" style={{ whiteSpace: 'nowrap' }}>
            {formatRs(advance.amountRs)}{' '}
            <button
              type="button"
              disabled={busy}
              style={{
                cursor: 'pointer',
                textDecoration: 'underline',
                background: 'none',
                border: 0,
                padding: 0,
                font: 'inherit',
                color: 'var(--text-danger)',
              }}
              onClick={() => {
                setCancelling(advance.id);
                setReason('');
                setTried(false);
              }}
            >
              Cancel
            </button>
          </div>
        ),
      )}
    </div>
  );
}
